import { Button, FormComboBox, FormSelect } from '@civilgrid/ui/components';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { createOrg as apiCreateOrg, type NewOrgData } from '~/api/internal';
import { PlanType, type Subscription } from '~/common/types';
import ControlledCheckbox from '~/components2/forms/controlled/ControlledCheckbox';
import ControlledInput from '~/components2/forms/controlled/ControlledInput';
import Form from '~/components2/forms/Form';
import { Notify } from '~/components2/Notify';
import { US_STATES } from '~/pages/admin/civilsearch/common/USStates';
import rootStore from '~/store';
import { toNumber, validateDecimal } from '../common/common';
import { MajorHeading } from '../common/Heading';
import { emailRegex, orgPlanTypes, splitByComma } from './common';

type TFormValues = {
  name: string;
  billingContacts: string;
  planType: PlanType;
  projectCost: Subscription['projectCost'];
  sfdcAccountId: string;
  sfdcOpportunityId: string;
  state: string | null;
  excludeDemoProject: boolean;
  isInternal: boolean;
};

const defaultProjectCost: Subscription['projectCost'] = {
  //@ts-expect-error
  proposal: {
    polygon: {
      urban: { unit: 20, cost: 250_00 },
      rural: { unit: 20, cost: 250_00 },
    },
    linear: {
      urban: { unit: 0.5, cost: 250_00 },
      rural: { unit: 0.5, cost: 250_00 },
    },
    circle: {
      urban: { unit: 0.25, cost: 250_00 },
      rural: { unit: 0.25, cost: 250_00 },
    },
  },
  //@ts-expect-error
  project: {
    polygon: {
      urban: { unit: 20, cost: 1250_00 },
      rural: { unit: 20, cost: 1250_00 },
    },
    linear: {
      urban: { unit: 0.5, cost: 1250_00 },
      rural: { unit: 0.5, cost: 1250_00 },
    },
    circle: {
      urban: { unit: 0.25, cost: 1250_00 },
      rural: { unit: 0.25, cost: 1250_00 },
    },
  },
  //@ts-expect-error
  projectPlus: {
    polygon: {
      urban: { unit: 20, cost: 2500_00 },
      rural: { unit: 20, cost: 2500_00 },
    },
    linear: {
      urban: { unit: 0.5, cost: 2500_00 },
      rural: { unit: 0.5, cost: 2500_00 },
    },
    circle: {
      urban: { unit: 0.25, cost: 2500_00 },
      rural: { unit: 0.25, cost: 2500_00 },
    },
  },
};

export default function CreateOrganization() {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);

  const form = useForm<TFormValues>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      billingContacts: '',
      planType: PlanType.Tier1,
      projectCost: { ...defaultProjectCost },
      sfdcAccountId: '',
      sfdcOpportunityId: '',
      state: '',
      excludeDemoProject: false,
      isInternal: false,
    },
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid, isDirty },
  } = form;

  const isInternal = watch('isInternal');
  const sfdcAccountId = watch('sfdcAccountId');

  const hasSalesforceIdOrIsInternal = isInternal || (sfdcAccountId?.trim() ?? '').length > 0;

  const onSubmit: SubmitHandler<TFormValues> = async (data) => {
    const sfdcId = data.sfdcAccountId?.trim() ?? '';
    if (!data.isInternal && sfdcId.length === 0) {
      Notify.error({ message: 'Either mark as internal or provide a Salesforce Account ID' });
      return;
    }

    try {
      setIsProcessing(true);
      const {
        projectCost: { proposal, project, projectPlus },
      } = data;

      const orgData: NewOrgData = {
        organizationName: data.name,
        billingContacts: splitByComma(data.billingContacts),
        planType: data.planType,
        zoningCost: 0,
        sfdcAccountId: data.isInternal ? null : data.sfdcAccountId.trim() || null,
        sfdcOpportunityId: data.isInternal ? null : data.sfdcOpportunityId.trim() || null,
        state: data.state || null,
        excludeDemoProject: data.excludeDemoProject,
        isInternal: data.isInternal,
        projectCost: {
          //@ts-expect-error
          proposal: {
            polygon: {
              urban: {
                unit: toNumber(proposal.polygon.urban.unit),
                cost: toNumber(proposal.polygon.urban.cost),
              },
              rural: {
                unit: toNumber(proposal.polygon.rural.unit),
                cost: toNumber(proposal.polygon.rural.cost),
              },
            },
            linear: {
              urban: {
                unit: toNumber(proposal.linear.urban.unit),
                cost: toNumber(proposal.linear.urban.cost),
              },
              rural: {
                unit: toNumber(proposal.linear.rural.unit),
                cost: toNumber(proposal.linear.rural.cost),
              },
            },
            circle: {
              urban: {
                unit: toNumber(proposal.circle.urban.unit),
                cost: toNumber(proposal.circle.urban.cost),
              },
              rural: {
                unit: toNumber(proposal.circle.rural.unit),
                cost: toNumber(proposal.circle.rural.cost),
              },
            },
          },
          //@ts-expect-error
          project: {
            polygon: {
              urban: {
                unit: toNumber(project.polygon.urban.unit),
                cost: toNumber(project.polygon.urban.cost),
              },
              rural: {
                unit: toNumber(project.polygon.rural.unit),
                cost: toNumber(project.polygon.rural.cost),
              },
            },
            linear: {
              urban: {
                unit: toNumber(project.linear.urban.unit),
                cost: toNumber(project.linear.urban.cost),
              },
              rural: {
                unit: toNumber(project.linear.rural.unit),
                cost: toNumber(project.linear.rural.cost),
              },
            },
            circle: {
              urban: {
                unit: toNumber(project.circle.urban.unit),
                cost: toNumber(project.circle.urban.cost),
              },
              rural: {
                unit: toNumber(project.circle.rural.unit),
                cost: toNumber(project.circle.rural.cost),
              },
            },
          },
          //@ts-expect-error
          projectPlus: {
            polygon: {
              urban: {
                unit: toNumber(projectPlus.polygon.urban.unit),
                cost: toNumber(projectPlus.polygon.urban.cost),
              },
              rural: {
                unit: toNumber(projectPlus.polygon.rural.unit),
                cost: toNumber(projectPlus.polygon.rural.cost),
              },
            },
            linear: {
              urban: {
                unit: toNumber(projectPlus.linear.urban.unit),
                cost: toNumber(projectPlus.linear.urban.cost),
              },
              rural: {
                unit: toNumber(projectPlus.linear.rural.unit),
                cost: toNumber(projectPlus.linear.rural.cost),
              },
            },
            circle: {
              urban: {
                unit: toNumber(projectPlus.circle.urban.unit),
                cost: toNumber(projectPlus.circle.urban.cost),
              },
              rural: {
                unit: toNumber(projectPlus.circle.rural.unit),
                cost: toNumber(projectPlus.circle.rural.cost),
              },
            },
          },
        },
      };

      const res = await apiCreateOrg(orgData);
      if (!res?.id) throw new Error('Bad Response!');

      await rootStore.refreshMe();
      await rootStore.setCurrentOrganization(res.id);

      Notify.info({ message: `New org ${res.name} created!` });
      form.reset({});
      navigate({ to: '/home' });
    } catch (e) {
      console.error('Error creating organization: ', e);
      Notify.error({ message: 'Error creating organization!' });
    } finally {
      setIsProcessing(false);
    }
  };

  const setToZeroCostOrg = () => {
    const tiers = Object.keys(form.getValues('projectCost')) as (keyof Subscription['projectCost'])[];

    tiers.forEach((tier) => {
      const projectTypes = Object.keys(
        form.getValues(`projectCost.${tier}`),
      ) as (keyof Subscription['projectCost'][typeof tier])[];

      projectTypes.forEach((projectType) => {
        const baseKey = `projectCost.${tier}.${projectType}` as const;
        form.setValue(`${baseKey}.urban.cost`, 0);
        form.setValue(`${baseKey}.rural.cost`, 0);
      });
    });
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mb-10">
      <div className="flex items-center space-x-2">
        <MajorHeading>Organization Details</MajorHeading>
        <Button
          type="submit"
          loading={isProcessing}
          disabled={isProcessing || !isValid || !isDirty || !hasSalesforceIdOrIsInternal}
          data-testid="create-org-submit"
        >
          Submit
        </Button>
        {!hasSalesforceIdOrIsInternal && isDirty && (
          <span className="text-red-500 text-sm">Either mark as internal or provide a Salesforce Account ID</span>
        )}
      </div>

      <ControlledInput control={control} name="name" isRequired label="Organization Name" />

      <FormComboBox
        control={control}
        name="state"
        label="Default State"
        options={[...US_STATES]}
        isClearable
        data-testid="create-org-state"
      />

      <ControlledInput
        control={control}
        name="billingContacts"
        isRequired
        label="Billing Contacts"
        validate={{
          isEmailList: (value: string) => {
            const emails = value.split(',').map((email) => email.trim());
            const invalid = emails.filter((email) => !emailRegex.test(email));
            return invalid.length === 0 || 'Invalid email format for one or more email addresses';
          },
        }}
      />

      <FormSelect
        control={control}
        name="planType"
        isRequired
        label="Plan Type"
        options={orgPlanTypes}
        data-testid="create-org-plan-type"
      />

      <ControlledCheckbox
        control={control}
        name="excludeDemoProject"
        label="Don't Load Demo Project"
        data-testid="create-org-exclude-demo-project"
      />

      <ControlledCheckbox
        control={control}
        name="isInternal"
        label="Internal organization"
        data-testid="create-org-is-internal"
      />

      {!isInternal && (
        <>
          <ControlledInput
            control={control}
            name="sfdcAccountId"
            label="SFDC Account ID"
            isRequired
            data-testid="create-org-sfdc-account-id"
          />

          <ControlledInput
            control={control}
            name="sfdcOpportunityId"
            label="SFDC Opportunity ID"
            isRequired
            data-testid="create-org-sfdc-opportunity-id"
          />
        </>
      )}

      <div className="relative w-full space-y-4 overflow-hidden">
        <Button type="button" onClick={setToZeroCostOrg} variant="secondary">
          Set to Zero Cost
        </Button>

        <hr />
        <MajorHeading>Project Lite Pricing</MajorHeading>

        <div className="flex space-x-4">
          <div className="flex w-1/4 flex-col space-y-2">
            <MajorHeading>Polygon</MajorHeading>
            <ControlledInput
              control={control}
              name="projectCost.proposal.polygon.urban.unit"
              isRequired
              label="Polygon Units (acres) - Urban"
            />

            <ControlledInput
              control={control}
              name="projectCost.proposal.polygon.urban.cost"
              isRequired
              validate={validateDecimal}
              label="Polygon Unit Cost (cents) - Urban"
            />

            <ControlledInput
              control={control}
              name="projectCost.proposal.polygon.rural.unit"
              isRequired
              label="Polygon Units (acres) - Rural"
            />

            <ControlledInput
              control={control}
              name="projectCost.proposal.polygon.rural.cost"
              isRequired
              label="Polygon Unit Cost (cents) - Rural"
              validate={validateDecimal}
            />
          </div>

          <div className="flex w-1/4 flex-col space-y-2">
            <MajorHeading>Linear</MajorHeading>
            <ControlledInput
              control={control}
              name="projectCost.proposal.linear.urban.unit"
              isRequired
              label="Linear Units (miles) - Urban"
            />

            <ControlledInput
              control={control}
              name="projectCost.proposal.linear.urban.cost"
              isRequired
              label="Linear Unit Cost (cents) - Urban"
              validate={validateDecimal}
            />

            <ControlledInput
              control={control}
              name="projectCost.proposal.linear.rural.unit"
              isRequired
              label="Linear Units (miles) - Rural"
            />

            <ControlledInput
              control={control}
              name="projectCost.proposal.linear.rural.cost"
              isRequired
              label="Linear Unit Cost (cents) - Rural"
              validate={validateDecimal}
            />
          </div>

          <div className="flex w-1/4 flex-col space-y-2">
            <MajorHeading>Circle</MajorHeading>
            <ControlledInput
              control={control}
              name="projectCost.proposal.circle.urban.unit"
              isRequired
              label="Circle Units (acres) - Urban"
            />

            <ControlledInput
              control={control}
              name="projectCost.proposal.circle.urban.cost"
              isRequired
              label="Circle Unit Cost (cents) - Urban"
              validate={validateDecimal}
            />

            <ControlledInput
              control={control}
              name="projectCost.proposal.circle.rural.unit"
              isRequired
              label="Circle Units (acres) - Rural"
            />

            <ControlledInput
              control={control}
              name="projectCost.proposal.circle.rural.cost"
              isRequired
              label="Circle Unit Cost (cents) - Rural"
              validate={validateDecimal}
            />
          </div>
        </div>

        <hr />
        <MajorHeading>Project Pricing</MajorHeading>

        <div className="flex space-x-4">
          <div className="flex w-1/4 flex-col space-y-2">
            <MajorHeading>Polygon</MajorHeading>
            <ControlledInput
              control={control}
              name="projectCost.project.polygon.urban.unit"
              isRequired
              label="Polygon Units (acres) - Urban"
            />

            <ControlledInput
              control={control}
              name="projectCost.project.polygon.urban.cost"
              isRequired
              label="Polygon Unit Cost (cents) - Urban"
              validate={validateDecimal}
            />

            <ControlledInput
              control={control}
              name="projectCost.project.polygon.rural.unit"
              isRequired
              label="Polygon Units (acres) - Rural"
            />

            <ControlledInput
              control={control}
              name="projectCost.project.polygon.rural.cost"
              isRequired
              label="Polygon Unit Cost (cents) - Rural"
              validate={validateDecimal}
            />
          </div>

          <div className="flex w-1/4 flex-col space-y-2">
            <MajorHeading>Linear</MajorHeading>
            <ControlledInput
              control={control}
              name="projectCost.project.linear.urban.unit"
              isRequired
              label="Linear Units (miles) - Urban"
            />

            <ControlledInput
              control={control}
              name="projectCost.project.linear.urban.cost"
              isRequired
              label="Linear Unit Cost (cents) - Urban"
              validate={validateDecimal}
            />

            <ControlledInput
              control={control}
              name="projectCost.project.linear.rural.unit"
              isRequired
              label="Linear Units (miles) - Rural"
            />

            <ControlledInput
              control={control}
              name="projectCost.project.linear.rural.cost"
              isRequired
              label="Linear Unit Cost (cents) - Rural"
              validate={validateDecimal}
            />
          </div>

          <div className="flex w-1/4 flex-col space-y-2">
            <MajorHeading>Circle</MajorHeading>
            <ControlledInput
              control={control}
              name="projectCost.project.circle.urban.unit"
              isRequired
              label="Circle Units (acres) - Urban"
            />

            <ControlledInput
              control={control}
              name="projectCost.project.circle.urban.cost"
              isRequired
              label="Circle Unit Cost (cents) - Urban"
              validate={validateDecimal}
            />

            <ControlledInput
              control={control}
              name="projectCost.project.circle.rural.unit"
              isRequired
              label="Circle Units (acres) - Rural"
            />

            <ControlledInput
              control={control}
              name="projectCost.project.circle.rural.cost"
              isRequired
              label="Circle Unit Cost (cents) - Rural"
              validate={validateDecimal}
            />
          </div>
        </div>

        <hr />
        <MajorHeading>Project Plus Pricing</MajorHeading>

        <div className="flex space-x-4">
          <div className="flex w-1/4 flex-col space-y-2">
            <MajorHeading>Polygon</MajorHeading>
            <ControlledInput
              control={control}
              name="projectCost.projectPlus.polygon.urban.unit"
              isRequired
              label="Polygon Units (acres) - Urban"
            />

            <ControlledInput
              control={control}
              name="projectCost.projectPlus.polygon.urban.cost"
              isRequired
              label="Polygon Unit Cost (cents) - Urban"
              validate={validateDecimal}
            />

            <ControlledInput
              control={control}
              name="projectCost.projectPlus.polygon.rural.unit"
              isRequired
              label="Polygon Units (acres) - Rural"
            />

            <ControlledInput
              control={control}
              name="projectCost.projectPlus.polygon.rural.cost"
              isRequired
              label="Polygon Unit Cost (cents) - Rural"
              validate={validateDecimal}
            />
          </div>

          <div className="flex w-1/4 flex-col space-y-2">
            <MajorHeading>Linear</MajorHeading>
            <ControlledInput
              control={control}
              name="projectCost.projectPlus.linear.urban.unit"
              isRequired
              label="Linear Units (miles) - Urban"
            />

            <ControlledInput
              control={control}
              name="projectCost.projectPlus.linear.urban.cost"
              isRequired
              label="Linear Unit Cost (cents) - Urban"
              validate={validateDecimal}
            />

            <ControlledInput
              control={control}
              name="projectCost.projectPlus.linear.rural.unit"
              isRequired
              label="Linear Units (miles) - Rural"
            />

            <ControlledInput
              control={control}
              name="projectCost.projectPlus.linear.rural.cost"
              isRequired
              label="Linear Unit Cost (cents) - Rural"
              validate={validateDecimal}
            />
          </div>

          <div className="flex w-1/4 flex-col space-y-2">
            <MajorHeading>Circle</MajorHeading>
            <ControlledInput
              control={control}
              name="projectCost.projectPlus.circle.urban.unit"
              isRequired
              label="Circle Units (acres) - Urban"
            />

            <ControlledInput
              control={control}
              name="projectCost.projectPlus.circle.urban.cost"
              isRequired
              label="Circle Unit Cost (cents) - Urban"
              validate={validateDecimal}
            />

            <ControlledInput
              control={control}
              name="projectCost.projectPlus.circle.rural.unit"
              isRequired
              label="Circle Units (acres) - Rural"
            />

            <ControlledInput
              control={control}
              name="projectCost.projectPlus.circle.rural.cost"
              isRequired
              label="Circle Unit Cost (cents) - Rural"
              validate={validateDecimal}
            />
          </div>
        </div>
      </div>
    </Form>
  );
}
